import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { HttpService } from '@static/services/http.service';
import { saveAs } from 'file-saver-es';
import moment from 'moment';
import { ProjectService } from './project.service';
import { SnackService } from '@static/services/snack.service';
import { MatDialog } from '@angular/material/dialog';
import { DateRangeComponent, IDateRangeInput } from '@shared/dialogs/date-range/date-range.component';
import { ObservableService } from '@static/services/observable.service';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@static/services/toast.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private _observableService: ObservableService = new ObservableService();
  private _httpService: HttpService = inject(HttpService);
  private _projectService: ProjectService = inject(ProjectService);
  private _snackService: SnackService = inject(SnackService);
  private _dialog: MatDialog = inject(MatDialog);
  private _toastService: ToastService = inject(ToastService);

  /**
   * Export the latest user access list
   */
  public exportUserAccessList(): void {
    const toastId = this._toastService.addToast('Exporting User Access List', undefined, true);
    this._httpService.getP<Blob>(`${environment.backendUri}/admin/export/accesslist`, {
      responseType: 'blob'
    }).then((res: Blob) => {
      const dateString = moment().format('YYYY-MM-DD');
      saveAs(res, `CtrlkeyAccess_${dateString}.xlsx`);
    }).catch((err: HttpErrorResponse) => {
      if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
        this._snackService.openErrorSnack('Insufficient permissions for this project', err);
      }
      else this._snackService.openErrorSnack('Failed to export', err);
    }).finally(() => {
      this._toastService.removeToast(toastId);
    });
  }

  /**
   * Export the latest project access list
   */
  public exportProjectAccessList(): void {
    const confirm = this._dialog.open(DateRangeComponent, { data: {
      title: `Select the date range for the Project Access Export`,
      showGroups: false,
      start: moment.utc().startOf('M'),
      end: moment.utc().endOf('M'),
      periodDuration: 1,
      periodKey: 'M',
    } as IDateRangeInput });
    this._observableService.subscribe(
      confirm.afterClosed(),
      (data?: [number, number, string]) => {
        if (data === undefined) return;
        const toastId = this._toastService.addToast('Exporting Project Access List', undefined, true);

        this._httpService.postP<Blob>(`${environment.backendUri}/admin/export/projaccesslist`, {
          start: data[0],
          end: data[1],
        }, {
          responseType: 'blob'
        }).then((res: Blob) => {
          const dateString = moment().format('YYYY-MM-DD');
          saveAs(res, `CtrlkeyProjectAccess_${dateString}.xlsx`);
        }).catch((err: HttpErrorResponse) => {
          if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
            this._snackService.openErrorSnack('Insufficient permissions for this project', err);
          }
          else this._snackService.openErrorSnack('Failed to export', err);
        }).finally(() => {
          this._toastService.removeToast(toastId);
        });
      }
    )
  }

  /**
   * Export the latest user access list
   */
  public exportProjectUserAccessList(): void {
    const toastId = this._toastService.addToast('Exporting Project User Access List', undefined, true);
    const uri = `${environment.backendUri}/project/${this._projectService.currentId}/export/accesslist`;
    this._httpService.getP<Blob>(uri, {
      responseType: 'blob'
    }).then((res: Blob) => {
      const dateString = moment().format('YYYY-MM-DD');
      const projectName = this._projectService.getCurrentShortName();
      saveAs(res, `${projectName}_AccessList_${dateString}.xlsx`);
    }).catch((err: HttpErrorResponse) => {
      if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
        this._snackService.openErrorSnack('Insufficient permissions for this project', err);
      }
      else this._snackService.openErrorSnack('Failed to export', err);
    }).finally(() => {
      this._toastService.removeToast(toastId);
    });
  }
}
